import React from 'react';
import { Box, Card, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider } from '@mui/material';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import ScienceIcon from '@mui/icons-material/Science';
import BugReportIcon from '@mui/icons-material/BugReport';
import GrassIcon from '@mui/icons-material/Grass';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

const TimelineEvents = ({
  fertilizerEvents = [],
  fungicideEvents = [],
  insecticideEvents = [],
  herbicideEvents = [],
  harvests = [],
  sales = [],
  startDate,
  endDate,
}) => {
  // Events are already filtered by date in the parent component, so we just combine them
  const allEvents = [
    ...fertilizerEvents.map(e => ({ ...e, _type: 'fertilizer' })),
    ...fungicideEvents.map(e => ({ ...e, _type: 'fungicide' })),
    ...insecticideEvents.map(e => ({ ...e, _type: 'insecticide' })),
    ...herbicideEvents.map(e => ({ ...e, _type: 'herbicide' })),
    ...harvests.map(e => ({ ...e, _type: 'harvest' })),
    ...sales.map(e => ({ ...e, _type: 'sale' })),
  ];
  
  allEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
  const iconMap = {
    fertilizer: <LocalFloristIcon />,
    fungicide: <ScienceIcon />,
    insecticide: <BugReportIcon />,
    herbicide: <GrassIcon />,
    harvest: <AgricultureIcon />,
    sale: <MonetizationOnIcon />,
  };
  const colorMap = {
    fertilizer: '#82ca9d',
    fungicide: '#8884d8',
    insecticide: '#ffc658',
    herbicide: '#d32f2f',
    harvest: '#4caf50',
    sale: '#ff9800',
  };
  const getDesc = (e) => {
    // Helper to get section/plot/variety/plant type
    const section = e.zone?.name || e.section_name || e.section || e.zone_name || '';
    const plot = e.plot?.name || e.plot_name || e.plot || '';
    const variety = e.variety_name_cl || e.variety_name || '';
    const plantType = e.plant_type_cl || e.plant_type || '';
    let details = [];
    if (section) details.push(`Section: ${section}`);
    if (plot) details.push(`Plot: ${plot}`);
    if (e._type === 'harvest') {
      if (variety) details.push(`Variety: ${variety}`);
      if (plantType) details.push(`Plant Type: ${plantType}`);
      return [
        `Harvest: ${e.harvest_amt || ''} ${e.unit_type || ''}`,
        ...details
      ].join(' | ');
    }
    if (e._type === 'sale') {
      if (variety) details.push(`Variety: ${variety}`);
      if (plantType) details.push(`Plant Type: ${plantType}`);
      return [
        `Sale: ${e.sale_amt || ''} ${e.sale_unit_type || ''} @ ₦${e.unit_price || ''}`,
        ...details
      ].join(' | ');
    }
    if (['fertilizer', 'fungicide', 'insecticide', 'herbicide'].includes(e._type)) {
      let label = '';
      if (e._type === 'fertilizer') label = `Fertilizer: ${e.fertilizer_name || ''}`;
      if (e._type === 'fungicide') label = `Fungicide: ${e.fungicide_name || ''}`;
      if (e._type === 'insecticide') label = `Insecticide: ${e.insecticide_name || ''}`;
      if (e._type === 'herbicide') label = `Herbicide: ${e.herbicide_name || ''}`;
      
      // Add amount and amount used information
      const amountInfo = [];
      if (e.amt_used) amountInfo.push(`Amount Used: ${e.amt_used} ${e.amt_used_unit_type || ''}`);
      if (e.event_cost) amountInfo.push(`Cost: ₦${e.event_cost}`);
      
      return [label, ...amountInfo, ...details].join(' | ');
    }
    return 'Event';
  };

  return (
    <Box sx={{
      backgroundColor: 'transparent',
      color: '#fff',
      borderRadius: 1.5,
      px: 4,
      pt: 0,
      pb: 0,
      mt: 0,
      minHeight: 200,
    }}>
      {allEvents.length === 0 ? (
        <Typography sx={{ color: '#AAB4C8', fontSize: 14 }}>No events found for the selected date range.</Typography>
      ) : (
        <List sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
          {allEvents.map((event, idx) => (
            <React.Fragment key={idx}>
              <ListItem alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: colorMap[event._type] || '#bdbdbd', color: '#181F2A' }}>
                    {iconMap[event._type]}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>
                      {event._type.charAt(0).toUpperCase() + event._type.slice(1)}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography sx={{ color: '#AAB4C8', fontSize: 13, display: 'inline' }}>
                        {getDesc(event)}
                      </Typography>
                      <Typography sx={{ color: '#AAB4C8', fontSize: 12, ml: 1, display: 'inline' }}>
                        {new Date(event.date).toLocaleDateString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              {idx < allEvents.length - 1 && <Divider variant="inset" component="li" sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
};

export default TimelineEvents; 